import {
  BrandAssetKind,
  BrandKitField,
  BrandKitFieldType,
  BrandKitFont,
  BrandKitLimits,
  BrandKitListItemType,
  HexColor,
  HttpUrl,
  changeBrandKitFieldType,
  isBrandKitFieldEmpty,
  isValidListItem,
} from "@arrowsup/platform-dtos";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import * as ss from "superstruct";
import { BrandAssetPicker } from "./brand-asset-picker";
import { BrandAssetFileLink, BrandAssetThumbnail } from "./brand-assets";
import { fieldTypeLabels, listItemTypeLabels } from "./brand-kit-field-value";

const colorHelp = "Use a hex color like #ff00ff";
const urlHelp = "Use a URL starting with https://";

const isColor = (v: string) => ss.is(v, HexColor);
const isUrl = (v: string) => ss.is(v, HttpUrl);

/** `#abc` -> `#aabbcc`, as the native color input only takes 6 digits. */
const toSixDigitHex = (color: string): string | undefined => {
  if (!isColor(color)) {
    return undefined;
  }
  if (color.length === 7) {
    return color;
  }
  return color.replace(/[0-9a-f]/gi, (c) => c + c);
};

export const ColorInput: React.FC<{
  label?: string;
  value?: string;
  onChange: (value?: string) => void;
}> = ({ label, value, onChange }) => {
  const text = value ?? "";
  const invalid = text !== "" && !isColor(text);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <input
        type="color"
        aria-label={label ?? "Pick a color"}
        value={toSixDigitHex(text) ?? "#000000"}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 48,
          height: 40,
          padding: 0,
          border: 0,
          background: "none",
        }}
      />
      <TextField
        label={label}
        size="small"
        value={text}
        placeholder="#ff00ff"
        onChange={(e) => onChange(e.target.value.trim() || undefined)}
        error={invalid}
        helperText={invalid ? colorHelp : undefined}
        sx={{ width: 180 }}
        inputProps={{
          "aria-label": label ?? "Hex color",
          style: { fontFamily: "monospace" },
        }}
      />
    </Stack>
  );
};

const UrlInput: React.FC<{
  label?: string;
  value?: string;
  onChange: (value?: string) => void;
}> = ({ label, value, onChange }) => {
  const text = value ?? "";
  const invalid = text !== "" && !isUrl(text);
  return (
    <TextField
      label={label}
      size="small"
      value={text}
      placeholder="https://"
      onChange={(e) => onChange(e.target.value.trim() || undefined)}
      error={invalid}
      helperText={invalid ? urlHelp : undefined}
      inputProps={{ "aria-label": label ?? "URL" }}
      fullWidth
    />
  );
};

const ImageInput: React.FC<{
  value?: string;
  onChange: (value?: string) => void;
}> = ({ value, onChange }) => {
  const [picking, setPicking] = useState(false);
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {value ? (
        <BrandAssetThumbnail id={value} />
      ) : (
        <Typography variant="body2" color="text.disabled">
          No image
        </Typography>
      )}
      <Button size="small" onClick={() => setPicking(true)}>
        {value ? "Change image" : "Choose image"}
      </Button>
      {value && (
        <Button size="small" onClick={() => onChange(undefined)}>
          Remove image
        </Button>
      )}
      <BrandAssetPicker
        open={picking}
        kind={BrandAssetKind.Image}
        onClose={() => setPicking(false)}
        onPick={(asset) => onChange(asset.id)}
      />
    </Stack>
  );
};

const ListItemInput: React.FC<{
  itemType: BrandKitListItemType;
  value: string;
  onChange: (value: string) => void;
}> = ({ itemType, value, onChange }) => {
  switch (itemType) {
    case BrandKitListItemType.Color:
      return <ColorInput value={value} onChange={(v) => onChange(v ?? "")} />;
    case BrandKitListItemType.Url:
      return <UrlInput value={value} onChange={(v) => onChange(v ?? "")} />;
    case BrandKitListItemType.Text: {
      const invalid = !isValidListItem(itemType, value);
      return (
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          error={invalid}
          helperText={invalid ? "Enter text or remove this item" : undefined}
          inputProps={{ "aria-label": "List item" }}
          fullWidth
          multiline
        />
      );
    }
    case BrandKitListItemType.Image:
      return <BrandAssetThumbnail id={value} size={64} />;
    // no default
  }
};

export const moveItem = <T,>(items: T[], from: number, to: number): T[] => {
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const ListInput: React.FC<{
  itemType: BrandKitListItemType;
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ itemType, value, onChange }) => {
  const [picking, setPicking] = useState(false);
  const add = () => {
    if (itemType === BrandKitListItemType.Image) {
      setPicking(true);
    } else {
      const item = itemType === BrandKitListItemType.Color ? "#000000" : "";
      onChange([...value, item]);
    }
  };

  return (
    <Stack spacing={1}>
      {value.map((item, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
          <Box flex={1}>
            <ListItemInput
              itemType={itemType}
              value={item}
              onChange={(v) =>
                onChange(value.map((old, j) => (j === i ? v : old)))
              }
            />
          </Box>
          <IconButton
            size="small"
            aria-label="Move up"
            disabled={i === 0}
            onClick={() => onChange(moveItem(value, i, i - 1))}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Move down"
            disabled={i === value.length - 1}
            onClick={() => onChange(moveItem(value, i, i + 1))}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Remove item"
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={value.length >= BrandKitLimits.listItems}
          onClick={add}
        >
          Add {listItemTypeLabels[itemType].toLowerCase()}
        </Button>
      </Box>
      <BrandAssetPicker
        open={picking}
        kind={BrandAssetKind.Image}
        onClose={() => setPicking(false)}
        onPick={(asset) => onChange([...value, asset.id])}
      />
    </Stack>
  );
};

const FontInput: React.FC<{
  value?: BrandKitFont;
  onChange: (value?: BrandKitFont) => void;
}> = ({ value, onChange }) => {
  const [picking, setPicking] = useState(false);
  const font: BrandKitFont = value ?? { family: "" };
  const update = (patch: Partial<BrandKitFont>) => {
    const next = { ...font, ...patch };
    const isSet = next.family || next.cssUrl || next.fallback || next.assetId;
    onChange(isSet ? next : undefined);
  };
  const cssUrl = font.cssUrl ?? "";
  const cssUrlInvalid = cssUrl !== "" && !isUrl(cssUrl);
  const familyMissing = value !== undefined && !font.family.trim();

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <TextField
          label="Family"
          size="small"
          value={font.family}
          placeholder="Inter"
          onChange={(e) => update({ family: e.target.value })}
          error={familyMissing}
          helperText={familyMissing ? "Enter the font family" : undefined}
          inputProps={{ maxLength: BrandKitLimits.labelLength }}
        />
        <TextField
          label="Fallback"
          size="small"
          value={font.fallback ?? ""}
          placeholder="sans-serif"
          onChange={(e) =>
            update({
              fallback: e.target.value.trim() ? e.target.value : undefined,
            })
          }
          inputProps={{ maxLength: BrandKitLimits.labelLength }}
        />
        <TextField
          label="Stylesheet URL"
          size="small"
          value={cssUrl}
          placeholder="https://fonts.googleapis.com/css2?family=Inter"
          onChange={(e) =>
            update({ cssUrl: e.target.value.trim() || undefined })
          }
          error={cssUrlInvalid}
          helperText={cssUrlInvalid ? urlHelp : "Optional, e.g. Google Fonts"}
          sx={{ flex: 1, minWidth: 280 }}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Font file:
        </Typography>
        {font.assetId ? (
          <BrandAssetFileLink id={font.assetId} />
        ) : (
          <Typography variant="body2" color="text.disabled">
            None
          </Typography>
        )}
        <Button size="small" onClick={() => setPicking(true)}>
          {font.assetId ? "Change file" : "Choose file"}
        </Button>
        {font.assetId && (
          <Button size="small" onClick={() => update({ assetId: undefined })}>
            Remove file
          </Button>
        )}
      </Stack>
      <BrandAssetPicker
        open={picking}
        kind={BrandAssetKind.Font}
        onClose={() => setPicking(false)}
        onPick={(asset) => update({ assetId: asset.id })}
      />
    </Stack>
  );
};

const FieldValueInput: React.FC<{
  field: BrandKitField;
  onChange: (field: BrandKitField) => void;
}> = ({ field, onChange }) => {
  switch (field.type) {
    case BrandKitFieldType.Text:
    case BrandKitFieldType.LongText: {
      const long = field.type === BrandKitFieldType.LongText;
      const max = long
        ? BrandKitLimits.longTextLength
        : BrandKitLimits.textLength;
      const tooLong = field.value.length > max;
      return (
        <TextField
          size="small"
          value={field.value}
          onChange={(e) => onChange({ ...field, value: e.target.value })}
          multiline={long}
          minRows={long ? 3 : undefined}
          error={tooLong}
          helperText={tooLong ? `At most ${max} characters` : undefined}
          inputProps={{ "aria-label": "Value" }}
          fullWidth
        />
      );
    }
    case BrandKitFieldType.Color:
      return (
        <ColorInput
          value={field.value}
          onChange={(value) => onChange({ ...field, value })}
        />
      );
    case BrandKitFieldType.Url:
      return (
        <UrlInput
          value={field.value}
          onChange={(value) => onChange({ ...field, value })}
        />
      );
    case BrandKitFieldType.Font:
      return (
        <FontInput
          value={field.value}
          onChange={(value) => onChange({ ...field, value })}
        />
      );
    case BrandKitFieldType.Image:
      return (
        <ImageInput
          value={field.value}
          onChange={(value) => onChange({ ...field, value })}
        />
      );
    case BrandKitFieldType.List:
      return (
        <ListInput
          itemType={field.itemType}
          value={field.value}
          onChange={(value) => onChange({ ...field, value })}
        />
      );
    // no default
  }
};

/** Select value for a type, with lists encoded as `list:<itemType>`. */
const typeOptionValue = (
  type: BrandKitFieldType,
  itemType = BrandKitListItemType.Text
) => (type === BrandKitFieldType.List ? `list:${itemType}` : type);

export const fieldTypeOptions = [
  ...Object.values(BrandKitFieldType)
    .filter((t) => t !== BrandKitFieldType.List)
    .map((type) => ({
      value: typeOptionValue(type),
      label: fieldTypeLabels[type],
      type,
      itemType: undefined,
    })),
  ...Object.values(BrandKitListItemType).map((itemType) => ({
    value: typeOptionValue(BrandKitFieldType.List, itemType),
    label: `List of ${listItemTypeLabels[itemType].toLowerCase()}`,
    type: BrandKitFieldType.List,
    itemType,
  })),
];

export const parseTypeOption = (value: string) =>
  fieldTypeOptions.find((o) => o.value === value) ?? fieldTypeOptions[0];

interface Props {
  field: BrandKitField;
  /** Flags a missing value; off until someone tries to save. */
  showErrors?: boolean;
  onChange: (field: BrandKitField) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

/** Edits one field's schema (label, type, description) and value. */
export const BrandKitFieldEditor: React.FC<Props> = ({
  field,
  showErrors,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}) => {
  const changeType = (option: string) => {
    const { type, itemType } = parseTypeOption(option);
    const { field: next, lossy } = changeBrandKitFieldType(
      field,
      type,
      itemType
    );
    if (
      lossy &&
      !window.confirm(
        `Part of "${field.label}"'s value doesn't fit the new type and will be lost. Continue?`
      )
    ) {
      return;
    }
    onChange(next);
  };

  return (
    <Card variant="outlined" component="section" aria-label={field.label}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Label"
              size="small"
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
              inputProps={{ maxLength: BrandKitLimits.labelLength }}
              error={!field.label.trim()}
              helperText={
                !field.label.trim() ? "Label is required" : `Key: ${field.key}`
              }
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Type"
              size="small"
              value={typeOptionValue(
                field.type,
                field.type === BrandKitFieldType.List
                  ? field.itemType
                  : undefined
              )}
              onChange={(e) => changeType(e.target.value)}
              sx={{ width: 180 }}
            >
              {fieldTypeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <Box flexShrink={0}>
              <Tooltip title="Move up">
                <span>
                  <IconButton disabled={!onMoveUp} onClick={onMoveUp}>
                    <ArrowUpwardIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton disabled={!onMoveDown} onClick={onMoveDown}>
                    <ArrowDownwardIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove field">
                <IconButton onClick={onRemove}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
          <TextField
            label="Description"
            size="small"
            value={field.description}
            onChange={(e) =>
              onChange({ ...field, description: e.target.value })
            }
            inputProps={{ maxLength: BrandKitLimits.descriptionLength }}
            helperText="How to use this field. Shown to editors and to AI tools."
            multiline
            fullWidth
          />
          <Box>
            {showErrors && isBrandKitFieldEmpty(field) ? (
              <Typography variant="caption" color="error">
                Value is required. Fill it in or remove this field.
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Value
              </Typography>
            )}
            <Box mt={0.5}>
              <FieldValueInput field={field} onChange={onChange} />
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
